"use client"

import { memo, useMemo } from "react"
import Link from "next/link"
import CustomImage from "@/components/ui/CustomImage"
import CategoryChip from "@/components/ui/CategoryChip"
import { catColor } from "@/lib/catColors"
import { useAuth } from "@/contexts/AuthContext"
import { getCategoryNames, getCategoryNamesEnglish } from "@/lib/helper"
import { getAllTagsFromStored } from "@/lib/newsTags"

const CategoryMegaMenu = ({
    categories = [],
    previewByCategoryId = {},
    activeCategoryId,
    onHoverCategory,
    onLeave,
}) => {
    const { categories: allCategories } = useAuth()

    const activeCategory = useMemo(
        () => categories.find((c) => c.id === activeCategoryId),
        [categories, activeCategoryId]
    )

    const activeNews = activeCategoryId ? previewByCategoryId[activeCategoryId] || [] : []

    if (!activeCategoryId || !activeCategory) return null

    const accent = catColor(activeCategory.name)

    const renderCategoryChips = (item) => {
        const existingIds = String(item?.categoryIds || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)

        if (item?.categoryId != null && item.categoryId !== '') {
            const primary = String(item.categoryId)
            if (!existingIds.includes(primary)) existingIds.unshift(primary)
        }

        const selectedId = activeCategory?.id != null ? String(activeCategory.id) : null
        const ids = []
        const seen = new Set()

        // Selected first only when actually assigned on this news
        if (selectedId && existingIds.includes(selectedId)) {
            ids.push(selectedId)
            seen.add(selectedId)
        }
        for (const id of existingIds) {
            if (seen.has(id)) continue
            seen.add(id)
            ids.push(id)
        }

        if (!ids.length) return null

        return (
            <div className="mega-news-card__chips">
                {ids.slice(0, 2).map((id) => {
                    const isSelected = selectedId && id === selectedId
                    let name = null
                    let slug = null

                    if (isSelected) {
                        name = activeCategory.name || getCategoryNames(id, allCategories)
                        slug =
                            activeCategory.slug ||
                            activeCategory.nameEnglish?.toLowerCase() ||
                            null
                    } else {
                        const category = allCategories.find((cat) => String(cat.id) === String(id))
                        name = category?.name || getCategoryNames(id, allCategories)
                        slug = category?.slug || getCategoryNamesEnglish(id, allCategories) || null
                    }

                    if (!name || !slug) return null
                    return (
                        <CategoryChip
                            key={`${item.id}-${id}`}
                            name={name}
                            url={`/category/${slug}`}
                            size="sm"
                        />
                    )
                })}
            </div>
        )
    }

    const renderTags = (item) => {
        const tags = getAllTagsFromStored(item?.tags).slice(0, 2)
        if (!tags.length) return null

        return (
            <div className="mega-news-card__tags">
                {tags.map((tag) => (
                    <span
                        key={`${item.id}-${tag}`}
                        className="mega-news-card__tag"
                        title={tag}
                    >
                        #{tag}
                    </span>
                ))}
            </div>
        )
    }

    return (
        <div
            className="absolute left-0 right-0 top-full z-200"
            onMouseEnter={() => onHoverCategory(activeCategoryId)}
            onMouseLeave={onLeave}
        >
            <div
                className="bg-white shadow-lg border border-(--border-default) border-t-[3px] rounded-b-md overflow-hidden"
                style={{ borderTopColor: accent }}
            >
                <div className="site-container flex items-center justify-between gap-3 py-2 border-b border-(--border-default) bg-(--surface-secondary)/50">
                    <h3 className="mr m-0 text-sm font-bold text-(--text-primary) truncate min-w-0">
                        {activeCategory.name}
                    </h3>
                    <Link
                        href={activeCategory.link}
                        className="mr text-[11px] font-semibold whitespace-nowrap shrink-0 hover:underline"
                        style={{ color: accent }}
                    >
                        अधिक बातम्या →
                    </Link>
                </div>

                <div className="site-container py-3">
                    {activeNews.length > 0 ? (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                            {activeNews.map((item, idx) => (
                                <div key={item.id || idx} className="mega-news-card group">
                                    <Link href={`/news/${item.slug}`} className="block">
                                        <div className="mega-news-card__media">
                                            <CustomImage
                                                src={item.featuredImage}
                                                alt={item.title}
                                                width={200}
                                                height={120}
                                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                                            />
                                        </div>
                                    </Link>
                                    <div className="mega-news-card__body">
                                        {renderCategoryChips(item)}
                                        <Link href={`/news/${item.slug}`} className="block">
                                            <p className="mega-news-card__title" title={item.title}>
                                                {item.title}
                                            </p>
                                        </Link>
                                        {renderTags(item)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="mr m-0 text-xs text-(--text-tertiary) py-5 text-center rounded-md bg-(--surface-secondary)">
                            या विभागात सध्या बातमी उपलब्ध नाही.
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}

export default memo(CategoryMegaMenu)
